import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';
import { RatingWidget } from '@/features/story/rating/RatingWidget';
import { submitBookRating } from '@/features/book/review/book-rating-api';
import { BookShareSheet, type CoverFile } from './BookShareSheet';

/**
 * Book-arrived review + share landing page.
 *
 * Reached via the deep link in the book-review follow-up mail
 * (`/book/:bookOrderId/review`). Owner-only — the rating POST and the
 * envelope GET are both owner-guarded server-side.
 *
 * Cover probing (Sebastian ruling 2026-10-21): the page fetches the
 * envelope once at mount, inspects `envelope.cover.imageUrl`, and
 * passes `coverAvailable` to the share sheet. When null (pre-render
 * states, failed renders, etc.) the Cover option is HIDDEN rather
 * than rendered with an error line.
 */
export default function BookReviewRoute() {
  const params = useParams<{ bookOrderId: string }>();
  const bookOrderId = params.bookOrderId ?? '';
  const copy = STRINGS.bookReview.page;
  const [shareOpen, setShareOpen] = useState(false);
  const [coverProbeState, setCoverProbeState] = useState<
    { kind: 'loading' } | { kind: 'available'; signedUrl: string } | { kind: 'unavailable' }
  >({ kind: 'loading' });
  const envelopeCacheRef = useRef<{ coverUrl: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!bookOrderId) return;
    (async () => {
      try {
        const envelope = await apiFetch<{
          cover: { imageUrl: string | null; imageIsPrintCover: boolean };
        }>(`/api/book/orders/${encodeURIComponent(bookOrderId)}`);
        if (cancelled) return;
        const url = envelope.cover.imageUrl;
        // Gate on `imageIsPrintCover` — true ONLY when the envelope's
        // cover.imageUrl points at the composed front cover
        // (AnchorType.cover with cartouche + title painted in). False
        // when the backend falls through to the raw book_anchor
        // (character portrait). Sharing a character portrait as if it
        // were the book cover would be wrong by construction.
        const isPrintCover = envelope.cover.imageIsPrintCover === true && url !== null;
        envelopeCacheRef.current = { coverUrl: isPrintCover ? url : null };
        setCoverProbeState(
          isPrintCover ? { kind: 'available', signedUrl: url } : { kind: 'unavailable' },
        );
      } catch {
        if (cancelled) return;
        setCoverProbeState({ kind: 'unavailable' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookOrderId]);

  const fetchCover = useCallback(async (): Promise<CoverFile | null> => {
    const probed = envelopeCacheRef.current?.coverUrl ?? null;
    if (!probed) return null;
    try {
      const res = await fetch(probed);
      if (!res.ok) return null;
      const blob = await res.blob();
      // Local-fs dev storage serves images with content-type
      // `application/octet-stream`; the browser's canShare() check
      // rejects that MIME, so force `image/png` (the production MIME
      // for anchor renders anyway — book covers are always PNG per
      // the render pipeline, see src/book/render-pipeline/).
      const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : 'image/png';
      const extension = type.includes('jpeg') || type.includes('jpg') ? 'jpg' : 'png';
      const file = new File([blob], `dreamable-cover-${bookOrderId}.${extension}`, {
        type,
      });
      return { file, url: probed };
    } catch {
      return null;
    }
  }, [bookOrderId]);

  if (!bookOrderId) {
    return (
      <main className="mx-auto max-w-xl px-4 py-10">
        <p className="text-dream-navy/80">{copy.notFound}</p>
      </main>
    );
  }

  const coverAvailable = coverProbeState.kind === 'available';

  return (
    <main className="mx-auto max-w-xl px-4 py-10">
      <h1 className="text-2xl font-semibold text-dream-navy">{copy.headline}</h1>
      <p className="mt-2 text-dream-navy/70">{copy.subtitle}</p>

      <section className="mt-6">
        <RatingWidget
          storyId={bookOrderId}
          initialRating={null}
          initialComment={null}
          initialRatedAt={null}
          onSubmitted={() => {
            // No parent state to update — RatingWidget's internal
            // alreadyRated state renders the thank-you line directly.
          }}
          submit={submitBookRating}
          headline={STRINGS.bookReview.ratingQuestion}
        />
      </section>

      <section className="mt-8 rounded-xl border border-dream-navy/10 bg-dream-white p-4">
        <h2 className="text-base font-semibold text-dream-navy">{copy.shareHeadline}</h2>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="mt-3 rounded-lg border border-dream-navy bg-dream-navy px-4 py-2 text-dream-white hover:bg-dream-navy/90"
        >
          {copy.shareButton}
        </button>
      </section>

      <BookShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        fetchCover={fetchCover}
        coverAvailable={coverAvailable}
      />
    </main>
  );
}
